import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Eye, Lock, Database, UserCheck, Bell, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';

const Privacy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-black pt-20 pb-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="text-white hover:text-gray-300 transition-colors mb-4 inline-flex items-center"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back
          </button>
          <div className="flex items-center space-x-3 mb-4">
            <Shield className="h-10 w-10 text-red-500" />
            <h1 className="text-4xl font-bold text-white">Privacy Policy</h1>
          </div>
          <p className="text-gray-400">Last updated: February 2025</p>
        </div>

        <div className="bg-white/5 backdrop-blur-sm rounded-xl p-8 border border-white/10 space-y-8 text-gray-300">
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
              <Eye className="h-6 w-6 mr-2 text-red-500" />
              1. Introduction
            </h2>
            <p className="mb-4">
              Seven Chapter Books ("we," "our," or "us") is committed to protecting your privacy. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
              when you use our digital book platform and services.
            </p>
            <p>
              Please read this Privacy Policy carefully. By using the Service, you consent to the 
              collection and use of information in accordance with this policy.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
              <Database className="h-6 w-6 mr-2 text-red-500" />
              2. Information We Collect
            </h2>
            
            <h3 className="text-xl font-semibold text-white mt-4 mb-2">Personal Information</h3>
            <p className="mb-4">When your account is created by an administrator, we collect:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Name</li>
              <li>Email address</li>
              <li>Account credentials (securely hashed)</li>
            </ul>

            <h3 className="text-xl font-semibold text-white mt-4 mb-2">Usage Data</h3>
            <p className="mb-4">We automatically collect certain information when you use the Service:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Reading progress and history</li>
              <li>Reading streaks and goal progress</li>
              <li>Book reviews and ratings you submit</li>
              <li>Device information and browser type</li>
              <li>IP address and approximate location</li>
              <li>Access times and pages viewed</li>
            </ul>
          </section>

          {/* How We Use Information */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
              <UserCheck className="h-6 w-6 mr-2 text-red-500" />
              3. How We Use Your Information
            </h2>
            <p className="mb-4">We use the collected information to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Provide and maintain our Service</li>
              <li>Track your reading progress across devices</li>
              <li>Calculate and display reading streaks and goals</li>
              <li>Display your reviews to other users</li>
              <li>Generate reading analytics for administrators</li>
              <li>Communicate with you about your account</li>
              <li>Detect, prevent, and address technical issues</li>
              <li>Improve our Service and user experience</li>
              <li>Enforce our Terms of Service</li>
            </ul>
          </section>

          {/* Data Sharing */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
              <Globe className="h-6 w-6 mr-2 text-red-500" />
              4. Information Sharing & Disclosure
            </h2>
            <p className="mb-4">We do NOT sell, trade, or rent your personal information to third parties.</p>
            <p className="mb-4">We may share your information in the following circumstances:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong className="text-white">With Administrators:</strong> Platform administrators can view user accounts, reading analytics, and reviews for management purposes.</li>
              <li><strong className="text-white">Public Reviews:</strong> Reviews and ratings you submit may be visible to other users.</li>
              <li><strong className="text-white">Legal Requirements:</strong> We may disclose information if required by law or to protect our rights.</li>
              <li><strong className="text-white">Service Providers:</strong> We may share data with trusted third parties who assist in operating our Service (e.g., email services for contact forms).</li>
            </ul>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
              <Lock className="h-6 w-6 mr-2 text-red-500" />
              5. Data Security
            </h2>
            <p className="mb-4">We implement appropriate security measures to protect your information:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Passwords are securely hashed using industry-standard encryption</li>
              <li>Data transmission is encrypted using HTTPS/TLS</li>
              <li>Access to personal data is restricted to authorized administrators</li>
              <li>Regular security assessments and updates</li>
            </ul>
            <p className="mt-4 text-yellow-400">
              <strong>Note:</strong> No method of transmission over the Internet is 100% secure. 
              While we strive to protect your information, we cannot guarantee absolute security.
            </p>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">6. Data Retention</h2>
            <p className="mb-4">We retain your information for as long as your account is active or as needed to provide our Service.</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Account data is retained until account deletion</li>
              <li>Reading progress data is retained to maintain your streaks and history</li>
              <li>Reviews are retained even after account deletion unless specifically requested</li>
              <li>Contact form submissions are retained for customer service purposes</li>
            </ul>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">7. Your Rights</h2>
            <p className="mb-4">You have the following rights regarding your personal data:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong className="text-white">Access:</strong> Request a copy of the personal data we hold about you</li>
              <li><strong className="text-white">Correction:</strong> Request correction of inaccurate personal data</li>
              <li><strong className="text-white">Deletion:</strong> Request deletion of your account and personal data</li>
              <li><strong className="text-white">Restriction:</strong> Request restriction of processing of your data</li>
            </ul>
            <p className="mt-4">
              To exercise these rights, please contact the administrator or use our 
              <a href="/contact" className="text-red-500 hover:text-red-400 ml-1">Contact Page</a>.
            </p>
          </section>

          {/* Cookies */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">8. Cookies & Local Storage</h2>
            <p className="mb-4">We use cookies and local storage to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Keep you signed in to your account</li>
              <li>Remember your preferences</li>
              <li>Track reading progress</li>
              <li>Improve Service performance</li>
            </ul>
            <p className="mt-4">
              You can control cookies through your browser settings, but disabling them may affect 
              Service functionality.
            </p>
          </section>

          {/* Children's Privacy */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">9. Children's Privacy</h2>
            <p>
              Our Service is not intended for children under 13 years of age. We do not knowingly 
              collect personal information from children under 13. If you are a parent or guardian 
              and believe your child has provided us with personal information, please contact us.
            </p>
          </section>

          {/* Updates */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
              <Bell className="h-6 w-6 mr-2 text-red-500" />
              10. Changes to This Policy
            </h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any changes 
              by posting the new Privacy Policy on this page and updating the "Last updated" date. 
              You are advised to review this Privacy Policy periodically for any changes.
            </p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">11. Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy or our data practices, please contact us through our 
              <a href="/contact" className="text-red-500 hover:text-red-400 ml-1">Contact Page</a>.
            </p>
          </section>
        </div>

        {/* Accept Button */}
        <div className="mt-8 text-center">
          <Button
            onClick={() => navigate('/')}
            className="bg-red-600 hover:bg-red-700 text-white px-8 py-3"
          >
            I Understand
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
